import React, { ReactNode } from "react";
import { Box } from "@mui/material";
import { LoadingLegendItem } from "./LoadingLegendItem";
import { LegendItem } from "./LegendItem";
import { LegendLayout } from "./LegendLayout";
import type { ClassificationLegendBreadcrumb } from "./LegendLayout";
import type { ClassificationBreakdownData } from "@components";

export interface ClassificationChartLegendProps {
  breadcrumbs: ClassificationLegendBreadcrumb[];
  chartTitle: string;
  chartTitleTooltipKey?: string;
  items: ClassificationBreakdownData[];
  rootTotalValue: number;
  isLoading?: boolean;
  hoveredItem?: ClassificationBreakdownData | null;
  renderBeforeEl?: (item: ClassificationBreakdownData) => ReactNode;
  onItemHover: (item: ClassificationBreakdownData | null) => void;
  onItemClick: (item: ClassificationBreakdownData) => void;
  getInvestmentHref?: (item: ClassificationBreakdownData) => string | undefined;
  isItemClickable?: (item: ClassificationBreakdownData) => boolean;
}

export const ClassificationChartLegend = ({
  breadcrumbs,
  chartTitle,
  chartTitleTooltipKey,
  items,
  rootTotalValue,
  isLoading = false,
  hoveredItem,
  renderBeforeEl,
  onItemHover,
  onItemClick,
  getInvestmentHref,
  isItemClickable,
}: ClassificationChartLegendProps) => {
  const getItemClickHandler = (item: ClassificationBreakdownData) => {
    if (isItemClickable && !isItemClickable(item)) return undefined;
    if (item.hasChildren) return () => onItemClick(item);
    return undefined;
  };

  const getItemHref = (item: ClassificationBreakdownData) => {
    if (item.hasChildren) return undefined;
    if (isItemClickable && !isItemClickable(item)) return undefined;
    return getInvestmentHref?.(item);
  };

  return (
    <Box p={0}>
      <LegendLayout
        breadcrumbs={breadcrumbs}
        chartTitle={chartTitle}
        chartTitleTooltipKey={chartTitleTooltipKey}
      >
        {isLoading
          ? Array.from({ length: 5 }).map((_, index) => (
              <LoadingLegendItem index={index} key={`loading-${index}`} />
            ))
          : items.map((item, index) => (
              <LegendItem
                key={item.uid}
                item={item}
                rootTotalValue={rootTotalValue}
                isSelected={hoveredItem?.uid === item.uid}
                beforeEl={renderBeforeEl?.(item)}
                isLast={index === items.length - 1}
                onMouseEnter={() => onItemHover(item)}
                onMouseLeave={() => onItemHover(null)}
                onClick={getItemClickHandler(item)}
                href={getItemHref(item)}
              />
            ))}
      </LegendLayout>
    </Box>
  );
};
